/**
 * API configuration
 *
 * In production web, CloudFront routes /api/* to ALB, so we use relative URLs.
 * For Capacitor/Desktop with a custom workspace domain, we use the stored domain as base URL.
 * Locally, we use the dev server URL from VITE_API_BASE_URL environment variable.
 */

import { getWorkspaceBaseUrl } from '../utils/workspaceDomain';

// Check for Capacitor native environment
const CapacitorGlobal = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
export const isCapacitor = !!(
  CapacitorGlobal?.isNativePlatform?.() ||
  window.location.protocol === 'capacitor:' ||
  window.location.protocol === 'ionic:'
);

// Check for Tauri desktop environment
const isDesktopApp = typeof window !== 'undefined' && (
  '__TAURI__' in window ||
  navigator.userAgent.toLowerCase().includes('tauri') ||
  localStorage.getItem('panluma_desktop_app') === 'true'
);

// Local dev: running on localhost (web dev server) or loading from a local/private IP
// (Capacitor pointing at local dev server via run-mobile-local.sh).
// In both cases the Vite dev server proxies /api/* to the backend, so relative URLs work.
const isLocalDev =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.protocol === 'http:';  // Production always uses https

// For Capacitor/Desktop apps with a custom workspace domain, use the stored domain.
// For web, use relative URLs (CloudFront routes /api/* to ALB).
// For local dev, use VITE_API_BASE_URL.
function resolveApiBaseUrl(): string {
  // In test environments (Vitest), always use the absolute URL so MSW handlers can match.
  if (import.meta.env.MODE === 'test') {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost';
  }
  // Capacitor production: the app loads BUNDLED assets from a local origin
  // (capacitor://localhost on iOS, https://localhost on Android), so relative
  // `/api/*` URLs would hit the bundle, not the backend. Always use an absolute
  // base pointing at the workspace domain (default app.panluma.ai), sent via
  // CapacitorHttp (enabled in capacitor.config.ts) to bypass cross-origin
  // restrictions. Local-dev Capacitor builds (run-mobile-local.sh) instead load
  // the LAN dev server over http and inject VITE_API_BASE_URL — those have
  // protocol 'http:' and fall through to the isLocalDev branch below.
  if (isCapacitor && window.location.protocol !== 'http:') {
    return import.meta.env.VITE_API_BASE_URL || getWorkspaceBaseUrl();
  }
  if (isLocalDev) {
    // Capacitor/desktop in local dev aren't served by the Vite dev server, so
    // they need the absolute backend URL.
    if (isCapacitor || isDesktopApp) {
      return import.meta.env.VITE_API_BASE_URL || '';
    }
    // Plain web dev: the Vite dev server proxies /api/* to the backend, so a
    // relative base keeps API calls same-origin with the page. This is required
    // for the HttpOnly mfa_device ("trust this browser") cookie — an absolute,
    // cross-origin base (e.g. http://127.0.0.1:5005 while the page is
    // http://localhost:3003, as run_local/start.sh exports VITE_API_BASE_URL)
    // makes the browser drop Set-Cookie and never send the cookie back.
    return '';
  }
  if (isCapacitor || isDesktopApp) {
    // Use stored workspace domain if set, otherwise default to app.panluma.ai.
    // When on default domain, relative URLs work fine so return empty.
    const stored = localStorage.getItem('panluma_workspace_domain');
    if (stored) {
      return getWorkspaceBaseUrl();
    }
  }
  return '';  // Empty = relative URLs
}

export const API_BASE_URL = resolveApiBaseUrl();

/**
 * Base URL (ws:// or wss://, no path) for WebSocket connections.
 *
 * WebSockets cannot go through CapacitorHttp, so on bundled native apps the WS
 * host MUST be derived from the absolute API base — `window.location.host` is
 * the bundled local origin (`localhost`), not the API server, which would make
 * every WS connect to the device itself and silently fail. So the absolute-base
 * branch is checked FIRST: bundled Capacitor has a `localhost` origin yet needs
 * the remote API host. Everything else (production web AND local dev with a
 * relative base) is same-origin — production via CloudFront, local dev via the
 * Vite `/api` ws-proxy.
 */
export function getWebSocketBaseUrl(): string {
  // Absolute API base (native apps, custom workspace domains): https→wss, http→ws.
  if (API_BASE_URL) {
    return API_BASE_URL.replace(/^http/, 'ws');
  }
  // Local dev with a relative API base: connect same-origin so the Vite dev
  // server's `/api` proxy (ws: true, see vite.config.ts) forwards the WS upgrade
  // to whatever backend it targets — 5005 on main, the worktree's own backend in
  // a PanClaude worktree (:31xx → :51xx). A hardcoded 5005 here would make every
  // worktree frontend open its notifications WS against the MAIN backend.
  // Production web (below) is also same-origin via CloudFront, so they share a path.
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

/** Whether the app is running as a native/desktop app that needs workspace selection.
 *  Native/desktop PRODUCTION builds load bundled assets from a local origin and must
 *  pick a workspace domain up front. Local-dev native builds (run-mobile-local.sh)
 *  load the LAN dev server over http with VITE_API_BASE_URL injected and talk to it
 *  directly, so they skip the selector.
 *  NOTE: do NOT gate on `isLocalDev` here — once mobile bundles its assets, the
 *  bundled origin's hostname is `localhost`, which makes `isLocalDev` true and would
 *  wrongly suppress the selector on production mobile. */
export const needsWorkspaceSelector =
  (isCapacitor || isDesktopApp)
  && window.location.protocol !== 'http:'
  && !import.meta.env.VITE_API_BASE_URL;

export const API_ENDPOINTS = {
  auth: {
    login: '/api/v1/auth/login',
    register: '/api/v1/auth/register',
    logout: '/api/v1/auth/logout',
    me: '/api/v1/auth/me',
    refresh: '/api/v1/auth/refresh',
    changePassword: '/api/v1/auth/change-password',
  },
} as const;
